/**
 * CHORDS (F1 — music capability). Pure transposition logic: each musician
 * stores chords/notes by role (`song_part.content`) and setlist
 * can set key per occasion (`key_override`) — transposition
 * is done ON THE FLY on the client, original content is not modified.
 */

const SHARP_SCALE = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;
const FLAT_SCALE = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"] as const;

/** Canonical keys for UI selects (12, with common enharmonics). */
export const MUSICAL_KEYS = [
  "C",
  "Db",
  "D",
  "Eb",
  "E",
  "F",
  "F#",
  "G",
  "Ab",
  "A",
  "Bb",
  "B",
] as const;
export type MusicalKey = (typeof MUSICAL_KEYS)[number];

const NOTE_INDEX = new Map<string, number>();
for (const [i, n] of SHARP_SCALE.entries()) {
  NOTE_INDEX.set(n, i);
}
for (const [i, n] of FLAT_SCALE.entries()) {
  NOTE_INDEX.set(n, i);
}

/** Chromatic index (0=C) of note `A-G(#|b)?`; `null` if not a note. */
export function noteIndex(note: string): number | null {
  const normalized = note.charAt(0).toUpperCase() + note.slice(1);
  return NOTE_INDEX.get(normalized) ?? null;
}

/** Is key written with flats? (decides spelling when transposing). */
export function prefersFlats(key: string): boolean {
  const root = key.replace(/m(aj)?$|m$/i, "");
  return root.includes("b") || ["F", "Dm", "Gm", "Cm", "Fm", "Bbm", "Ebm"].includes(key);
}

/** Semitones (0..11) to go from `fromKey` to `toKey`. */
export function semitonesBetween(fromKey: string, toKey: string): number {
  const from = noteIndex(fromKey.replace(/m$/i, ""));
  const to = noteIndex(toKey.replace(/m$/i, ""));
  if (from === null || to === null) {
    return 0;
  }
  return (to - from + 12) % 12;
}

/** Transpone una nota N semitonos. */
export function transposeNote(note: string, semitones: number, useFlats: boolean): string {
  const idx = noteIndex(note);
  if (idx === null) {
    return note;
  }
  const scale = useFlats ? FLAT_SCALE : SHARP_SCALE;
  return scale[(idx + semitones + 120) % 12] ?? note;
}

const CHORD_RE =
  /^([A-G](?:#|b)?)((?:maj|min|dim|aug|sus|add|m|M|\+|-|°|ø|\d|\(|\)|#|b)*)(?:\/([A-G](?:#|b)?))?$/;

export interface ParsedChord {
  root: string;
  quality: string;
  bass: string | null;
}

/** Parses chord token (`Am7`, `F#m7b5`, `C/G`); `null` if not. */
export function parseChordToken(token: string): ParsedChord | null {
  const m = CHORD_RE.exec(token);
  if (!m) {
    return null;
  }
  return { root: m[1] ?? "", quality: m[2] ?? "", bass: m[3] ?? null };
}

/** Transposes ONE chord token; non-chord tokens returned unchanged. */
export function transposeChordToken(token: string, semitones: number, useFlats: boolean): string {
  const chord = parseChordToken(token);
  if (!chord) {
    return token;
  }
  const root = transposeNote(chord.root, semitones, useFlats);
  const bass = chord.bass ? `/${transposeNote(chord.bass, semitones, useFlats)}` : "";
  return `${root}${chord.quality}${bass}`;
}

/** "Neutral" tokens allowed in a chord line (separators/annotations). */
const NEUTRAL_TOKEN_RE = /^(\||-|–|—|%|\.|,|\(|\)|x\d+|N\.?C\.?|\/)$/i;

/**
 * Is chord line? (all tokens are chords or separators, and contains
 * at least one chord). Leaves lyrics lines intact during transposition.
 */
export function isChordLine(line: string): boolean {
  const tokens = line.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return false;
  }
  let chords = 0;
  for (const token of tokens) {
    if (parseChordToken(token)) {
      chords += 1;
    } else if (!NEUTRAL_TOKEN_RE.test(token)) {
      return false;
    }
  }
  return chords > 0;
}

/**
 * Transposes mixed chords+lyrics content: ONLY chord lines
 * change; lyrics and annotations remain intact. Preserves internal
 * spacing of each line (chords align over syllable).
 */
export function transposeContent(content: string, semitones: number, useFlats: boolean): string {
  if (semitones % 12 === 0) {
    return content;
  }
  return content
    .split("\n")
    .map((line) => {
      if (!isChordLine(line)) {
        return line;
      }
      return line.replaceAll(/\S+/g, (token) => transposeChordToken(token, semitones, useFlats));
    })
    .join("\n");
}
